import {
	Button,
	FormProvider,
	Link,
	NotificationCard,
	Section,
} from "@arp/react-ndbx";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Fade, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import {
  AURA_VALIDATION_MESSAGES,
  Application,
  TRACKIFY_EVENT,
  optVerificationModalContent,
  URLS
} from "../../../config/const/enums";
import { queryKeys } from "../../../hooks/dataHooks/queryKeys";
import useSharedData from "../../../hooks/useSharedData";
import { PATH_ONLINE } from "../../../routes/paths";
import IdentityHelper from "../../../utilities/IdentityHelper";
import { formatMobileNumberfromOktaMasked } from "../../../utilities/mappingHelper";
import {
	getOktaSmsTriggerErrorCode,
	oktaAuthenticate,
	oktaProceed,
	oktaProceedAuthenticator,
	oktaTriggerSMS,
	oktaVerifyOtp,
} from "../../../utilities/oktaVerificationUtil";
import { RHFCodeInput } from "../../coding/rhfCodeInput";
import { trackifyPageChange } from "@allianz/tracking";

function RetrieveOtpVerificationCodeModal({
	userMobileNumber,
	quoteRefNumber,
}) {
	const queryClient = useQueryClient();
	const { handleEventChange } = useSharedData();
	const navigate = useNavigate();
	const [smsVerificationFailed, setSmsVerificationFailed] = useState(false);
	const [smsVerificationErrMsg, setSmsVerificationErrMsg] = useState("");
	const [resendSMSFailed, setResendSMSFailed] = useState(false);
	const [resendSMSFailedMessage, setResendSMSFailedMessage] = useState("");
	const [successResendOtp, setSuccessResendOtp] = useState(false);
	const [disableResendButton, setDisableResendButton] = useState(false);
	const [validateOtpLoading, setValidateOtpLoading] = useState(false);

	const schema = yup.object().shape({
		verificationCode: yup
			.string()
			.required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED)
			.length(6, ""),
	});

	const methods = useForm({
		resolver: yupResolver(schema),
		mode: "all",
		reValidateMode: "all",
		// shouldUnregister: true,
	});

	const {
		control,
		formState: { isValid },
		reset,
		getValues,
	} = methods;

	const validateOtp = async () => {
		try {
			methods.trigger().then(async (isValid) => {
				if (isValid) {
					setValidateOtpLoading(true);
					const formData = getValues();
					const oktaAuthClient = IdentityHelper.getOktaAuthClient();
					const otpVerificationres = await oktaVerifyOtp(
						oktaAuthClient,
						formData.verificationCode
					);
					if (otpVerificationres.status === "SUCCESS") {
						/*redirect the user to the retriev loading page
						 */
						const payload = {
							oktaAccessToken:
								otpVerificationres?.tokens?.accessToken?.accessToken,
							transactionUid: null,
							expired: null,
							application: Application.ONLINE,
							quoteNumber: quoteRefNumber,
						};
						/*
              need to set the payload in tanstack and call retrieve quote API in the RetrieveLoading page.
              In the RetrieveLoading loading page we will determine the status and redirect the user
            */
						sessionStorage.setItem("retrieveQuoteJourney", true);
						handleEventChange({
							name: "retrieveQuoteJourney",
							value: true,
						});
						queryClient.setQueryData(
							[queryKeys.RETRIEVE_QUOTE_TOKEN_DETAIL],
							payload || {}
						);
						navigate(PATH_ONLINE.retrieveQuoteLoading);
					} else {
						setValidateOtpLoading(false);
						reset();
						setSmsVerificationFailed(true);
						setSuccessResendOtp(false);
						const nextStepDetails = _.first(otpVerificationres.nextStep);
						// if OTP validation failed add OTP_VERIFICATION_FAILED message else a generic message
						nextStepDetails.name === "verificationCode"
							? setSmsVerificationErrMsg(
									optVerificationModalContent.OTP_VERIFICATION_FAILED
								)
							: setSmsVerificationErrMsg(
									optVerificationModalContent.GENERIC_CODE_VERIFICATION_ERROR
								);
					}
				}
			});
		} catch (error) {
			console.error("Error while submitting the OTP - ", error);
			setSmsVerificationFailed(true);
			setSmsVerificationErrMsg(
				optVerificationModalContent.GENERIC_CODE_VERIFICATION_ERROR
			);
			setValidateOtpLoading(false);
		}
	};

	const resendOtp = async () => {
		try {
			setDisableResendButton(true);
			reset();
			IdentityHelper.clearStorage(Application.ONLINE);
			IdentityHelper.initOktaConfig(Application.ONLINE);
			let oktaClient = IdentityHelper.getOktaAuthClient();
			await oktaAuthenticate(oktaClient);
			await oktaProceed(oktaClient, quoteRefNumber);
			await oktaProceedAuthenticator(oktaClient);
			const smsSendResponse = await oktaTriggerSMS(oktaClient);
			if (smsSendResponse.status === "TERMINAL") {
				setSuccessResendOtp(false);
				setResendSMSFailed(true);
				const oktaErrorCode = getOktaSmsTriggerErrorCode(
					smsSendResponse?.messages
				);
				if (oktaErrorCode === "errors.E0000150") {
					// Error message from ok "You have reached the limit of sms requests, please try again later."
					setResendSMSFailedMessage(optVerificationModalContent.SMS_SEND_WAIT);
				} else {
					setResendSMSFailedMessage(
						optVerificationModalContent.GENERIC_SMS_ERROR
					);
				}
			} else {
				setSuccessResendOtp(true);
				setSmsVerificationFailed(false);
				setResendSMSFailed(false);
			}
		} catch (error) {
			setResendSMSFailed(true);
			setResendSMSFailedMessage(optVerificationModalContent.GENERIC_SMS_ERROR);
		}
		// enable the resend sms link after 30 sec as per Okta guidelines
		setTimeout(() => {
			setDisableResendButton(false);
		}, 30000);
	};

	useEffect(() => {
		trackifyPageChange(TRACKIFY_EVENT.RETRIEVE_OTP_VALIDATION_MODAL);
	}, []);

  return (
    <FormProvider methods={methods}>
      <Section
        controlIdentifier="otp-verification-code"
        sx={{
          pt: { xs: 5, sm: 8 },
          mt: 0,
          mb: 0,
        }}
        onKeyDownCapture={(e) => {
					if (isValid) validateOtp();
				}}
      >
        <Section
          sx={{
            mt: 0,
            mb: 5,
            pl: { xs: 3, sm: 8 },
            pr: { xs: 3, sm: 8 },
          }}
        >
          <Typography variant="h3-bold">
            {optVerificationModalContent.HELP_CONFIRM_IDENTITY}
          </Typography>
        </Section>
        <Section
          sx={{
            mt: 0,
            mb: 5,
            pl: { xs: 3, sm: 8 },
            pr: { xs: 3, sm: 8 },
          }}
        >
          <Typography variant="label-large-regular">
            {optVerificationModalContent.ENTER_CODE}{" "}
            {formatMobileNumberfromOktaMasked(userMobileNumber)}
          </Typography>
        </Section>
        <Section
          mb={{ xs: 5, md: 8 }}
          sx={{
            pl: { xs: 1, sm: 8 },
            pr: { xs: 1, sm: 8 },
          }}
        >
          <RHFCodeInput
            name="verificationCode"
            className="verification-code"
            label="Verification Code"
            digitCodeNumber={6}
          />
        </Section>
        {"true" === import.meta.env.VITE_SKIP_SEND_SMS_MODAL && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="info"
                  title="Important information"
                  message={
                    <>
                      If the information you've provided is incorrect, call our
                      friendly Life Insurance Specialists on{" "}
                      <Link href={URLS.CONTACT_US_PHONE_URL}>
                        {URLS.CONTACT_US_PHONE_NUMBER}
                      </Link>{" "}
                      to discuss your application.
                    </>
                  }
                />
              </Box>
            </Fade>
          </Section>
        )}
        {smsVerificationFailed && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="error"
                  title=""
                  message={smsVerificationErrMsg}
                />
              </Box>
            </Fade>
          </Section>
        )}
        {successResendOtp && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="success"
                  title=""
                  message={optVerificationModalContent.RESEND_SMS_CODE}
                />
              </Box>
            </Fade>
          </Section>
        )}
        {resendSMSFailed && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="error"
                  title=""
                  message={resendSMSFailedMessage}
                />
              </Box>
            </Fade>
          </Section>
        )}
        <Section mb={3}>
          <Section sx={{ maxWidth: { xs: "250px" } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={validateOtp}
              disabled={!isValid || validateOtpLoading}
            >
              NEXT
            </Button>
          </Section>
        </Section>
        <Section sx={{ mb: 2, mt: 0 }}>
          <Typography variant="p3">
            {optVerificationModalContent.DIDNT_RECIEVE_CODE}
          </Typography>
        </Section>
        <Section>
          <Typography variant="p3">
            <Link
              disabled={disableResendButton}
              onClick={resendOtp}
              component="button"
              type="button"
            >
              {optVerificationModalContent.RESEND_CODE_PHONE}
            </Link>
          </Typography>
        </Section>
      </Section>
    </FormProvider>
  );
}

export default RetrieveOtpVerificationCodeModal;
